import AuthSubjectDialog from "@/components/AuthSubject";
import HBMessage from "@/components/HBMessage";
import LoginDialog from "@/components/LoginModal";
import MaterialDownloadDialog from "@/components/MaterialDownloadModal";
import { MutiSubjectTipModal } from "@/components/MaterialDownloadModal/MutiSubjectTipModal";
import SingleCategoryModal from "@/components/PinDetail/SingleCategoryModal";
import { BuyVipDialog } from "@/components/Vip";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import { getVipTypeById, PERSONAL_NO_AUTH } from "@/constants/vip";
import useAppStore from "@/context/store";
import {
  getBizDownloadList,
  getBizUserRightList,
  getCertList,
  getCurrentVip,
  getPreDownloadInfo,
} from "@/services";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

export function getMaterialFiles(pin: IPin) {
  const info = pin.material_info;
  const material = pin.file_material;

  let files = [];

  if (
    material.material_category === "平面模板" ||
    material.material_category === "视频模板"
  ) {
    const file = info?.files?.find((file) => file.file_type === "ORIGIN");
    if (file) {
      files.push(file);
    }
  } else if (material.material_category === "视频") {
    const mps = info?.content?.mps?.find((mps) => {
      return mps.name === "HD";
    });
    if (mps) {
      files.push({
        file_format: mps.file_format,
        file_size: mps.file_size,
        file_type: mps.name,
      });
    }
  } else if (
    material.material_category === "图片" &&
    // 兼容一批背景图逻辑
    info.type !== "background"
  ) {
    // 兼容AIGC图片
    if (info?.authorization?.name?.endsWith("AIGC")) {
      files = info.files;
    } else {
      const content = info.content;
      if (content.originWidth && content.originHeight) {
        files.push({
          file_format: content.format,
          file_size: content.originSize,
          file_type: "ORIGIN",
        });
      }
    }
  } else {
    files = info.files;
  }

  return files;
}

export function handleDownloadFileName(pin: IPin, file) {
  let name = [
    "花瓣素材",
    // 限制文件名长度，避免文件名过长
    pin?.raw_text?.substring(0, 50) || new Date().getTime(),
    pin.material_info?.id,
  ].join("_");
  let ext = file?.file_format ? "." + file?.file_format : "";

  let filename = name + ext;

  // 非法字符替换（Windows下不允许以下字符：\/:*?"<>|，Mac OS下不允许以下字符：/）
  filename = filename.replace(/[/:*?"<>|]/g, "-");

  //判断文件名是否以空格结尾（不允许）
  filename = filename.replace(/\s+$/, "");

  return filename;
}

export const getTypeNum = (category) => {
  switch (category) {
    case "图片":
      return 1;
    case "视频":
      return 3;
    case "平面模板":
      return 4;
    case "高端免抠":
      return 5;
    case "视频模板":
      return 6;
  }
};

export function aLinkDownload(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.click();
  a.remove();
}

const downloadDialogRef = { current: null };
export async function handleDownload(pin: IPin, file) {
  // 判断登录
  const user = useAppStore.getState().user;

  if (!user) {
    LoginDialog.open({
      onOk() {
        handleDownload(pin, file);
      },
    });

    return;
  }

  // 获取用户权益信息
  const [userRights, vips] = await Promise.all([
    getBizUserRightList({
      page_size: 200,
      status: 1,
      not_expired: 1,
    }),
    getCurrentVip(),
  ]);

  const material_category = pin?.file_material?.material_category;

  // 是否有会员
  if (!(vips?.length > 0)) {
    const isModel =
      material_category === "视频模板" || material_category === "平面模板";

    BuyVipDialog.open({
      // 模板类型素材，默认选中模板会员
      // 目前第一个versionTab为全站会员，第二个为单品类会员，若后续有变动，需要修改
      // versionTab: isModel ? "1" : "0",
      hideSingleCategory: !isModel,

      onFinish: () => handleDownload(pin, file),
    });

    return;
  }

  // 有商用授权
  const hasAuth = !userRights.list.find(
    (item) => item.code === PERSONAL_NO_AUTH
  );

  let subjectList = null;
  let latestCertSubjectId = null;

  // 商用授权需要检查授权主体
  if (hasAuth) {
    // 判断是否需要填写授权主体
    const subjectRes = await getCertList({ valid: 1, page_size: 200 });

    if (!subjectRes.list?.length) {
      return HBMessage.error("未获取到授权主体信息，请稍后再试");
    }

    subjectList = subjectRes.list;

    const validSubject = subjectRes.list.filter(
      (item) => item.subject_info?.id_no
    );

    if (validSubject.length === 0) {
      // 需要填写授权主体
      AuthSubjectDialog.open({
        vipId: vips[0].vip_id,
        certSubject: subjectRes.list[0],
        onOk: () => handleDownload(pin, file),
      });

      return;
    }

    // 未填写授权主题
    const noInfoSubject = subjectRes.list.find(
      (item) => !item.subject_info?.id_no
    );

    // 当存在多主体时，且有未填写授权主体时，弹窗提示
    if (
      subjectRes.list.length > 1 &&
      noInfoSubject &&
      !localStorage.getItem(LOCAL_STORAGE_KEY.HB_MUTI_SUBJECT_TIP)
    ) {
      try {
        await MutiSubjectTipModal.asyncOpen({
          subjects: subjectRes.list,
        });
      } catch (error) {
        return;
      }
    }

    latestCertSubjectId = validSubject[0].id;
    // 当存在多个授权主体时，需要默认选中最近使用的授权主体
    if (validSubject.length > 1) {
      // 通过最近下载记录获取最近使用的授权主体
      const res = await getBizDownloadList({ page_size: 1 });
      if (res.list?.length) {
        const id = res.list[0].vip_cert_subject_id;
        if (validSubject.find((item) => item.id === id)) {
          latestCertSubjectId = id;
        }
      }
    }
  }

  const info = pin?.material_info;
  const material = pin?.file_material;

  // 调用预下载接口，判断是否可以下载
  const result = await getPreDownloadInfo({
    material_id: info?.id,
    download_type: 1,
    type: getTypeNum(material?.material_category),
    vip_cert_subject_id: latestCertSubjectId,
  });

  if (result.err) {
    HBMessage.error("当前素材无法被下载");
    return;
  }

  // 判断会员有没有，下载权益有没有
  if (result.vips.length <= 0 || result.rights.length <= 0) {
    BuyVipDialog.open({
      onFinish: () => handleDownload(pin, file),
    });

    return;
  }

  // 单品类会员是否有下载权益
  if (!result.is_has_right_package) {
    SingleCategoryModal.open({
      materialType:
        material.material_category === "高端免抠"
          ? "免抠元素"
          : material.material_category,
      currentVipType: getVipTypeById(vips[0].vip_id),
    });

    return;
  }

  // 避免触发多个弹窗
  if (downloadDialogRef.current?.destroy) {
    downloadDialogRef.current.destroy();
  }

  downloadDialogRef.current = MaterialDownloadDialog.open({
    pin,
    info: result,
    file,
    userRights: userRights.list,
    subjects: subjectList,
    defaultSelectSubject: latestCertSubjectId,
    onDownload: () => handleDownload(pin, file),
  });
}
